import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { trpc } from "@/utils/trpc";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckSquare,
  Clock,
  FileText,
  KeyRound,
  Loader2,
  MapPin,
  Search,
  Target,
  Ticket as TicketIcon,
  Upload,
  UserCheck,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/utils/utils";
import {
  saveTicketDraft,
  getTicketDraft,
  clearTicketDraft,
  hasTicketDraft,
  getTicketDraftTimestamp,
  debounce
} from "@/utils/draftStorage";

// ============================================================================
// DESIGN TOKENS (Permit3 Service Desk — matches AllTickets / ticketing mockup)
// ============================================================================

const STEPS = [
  { key: "type", label: "categoryAndType" },
  { key: "details", label: "details" },
  { key: "review", label: "reviewAndSubmit" },
] as const;

const PRIORITIES = [
  { value: "p1", short: "P1", label: "critical", color: "#dc2626" },
  { value: "p2", short: "P2", label: "high", color: "#ea580c" },
  { value: "p3", short: "P3", label: "medium", color: "#0891b2" },
  { value: "p4", short: "P4", label: "low", color: "#65a30d" },
] as const;

// Map a ticket-type / category name to an icon + tinted swatch so the type
// grid reads like the mockup even though the DB stores no icon per type.
function typeVisual(name: string): { icon: LucideIcon; bg: string; fg: string } {
  const n = name.toLowerCase();
  if (/(incident|outage|broken|down|fault|alarm)/.test(n))
    return { icon: AlertTriangle, bg: "#fee2e2", fg: "#b91c1c" };
  if (/(change|update|deploy|migration)/.test(n))
    return { icon: Wrench, bg: "#fef3c7", fg: "#92400e" };
  if (/(problem|root cause|recurring)/.test(n))
    return { icon: Search, bg: "#ede9fe", fg: "#6d28d9" };
  if (/(task|work item|follow.?up|maintenance|pm)/.test(n))
    return { icon: CheckSquare, bg: "#d1fae5", fg: "#065f46" };
  if (/(access|permission|onboard|badge|credential)/.test(n))
    return { icon: KeyRound, bg: "#fce7f3", fg: "#9f1239" };
  if (/(request|service|supply|provision)/.test(n))
    return { icon: FileText, bg: "#dbeafe", fg: "#1e40af" };
  return { icon: TicketIcon, bg: "#ede9fe", fg: "#6d28d9" };
}

function fmtHours(hrs: number | null | undefined): string | null {
  if (hrs == null) return null;
  if (hrs < 1) return `${Math.round(hrs * 60)} minutes`;
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"}`;
  const d = Math.floor(hrs / 24);
  const r = hrs % 24;
  return r ? `${d}d ${r}h` : `${d} day${d === 1 ? "" : "s"}`;
}

const selectCls =
  "w-full px-3 py-2.5 rounded-lg border border-[#ece9f5] bg-white text-[13.5px] text-[#1a1530] outline-none focus:border-[#8b5cf6] focus:shadow-[0_0_0_3px_#ede9fe] transition appearance-none bg-no-repeat bg-[right_0.75rem_center] bg-[length:12px] cursor-pointer";
const inputCls =
  "w-full px-3 py-2.5 rounded-lg border border-[#ece9f5] bg-white text-[13.5px] text-[#1a1530] outline-none focus:border-[#8b5cf6] focus:shadow-[0_0_0_3px_#ede9fe] transition placeholder:text-[#b8b2c9]";
const chevronBg = {
  backgroundImage:
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b6386' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
};

// ============================================================================
// MAIN
// ============================================================================

export default function NewTicketForm() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const [step, setStep] = useState(0);

  // Form state
  const [categoryId, setCategoryId] = useState<string>("");
  const [typeId, setTypeId] = useState<string>("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<string>("");
  const [siteIds, setSiteIds] = useState<string[]>([]);
  const [showSitesModal, setShowSitesModal] = useState(false);
  const [sitesSearchQuery, setSitesSearchQuery] = useState("");
  const [dropdownAbove, setDropdownAbove] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [attachments, setAttachments] = useState<File[]>([]);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [showDraftIndicator, setShowDraftIndicator] = useState(false);
  const sitesButtonRef = useRef<HTMLButtonElement>(null);

  // Load draft on component mount
  useEffect(() => {
    if (draftLoaded) return;

    const draft = getTicketDraft(step);
    if (draft) {
      // Restore draft data
      setCategoryId(draft.data.categoryId ?? "");
      setTypeId(draft.data.typeId ?? "");
      setTitle(draft.data.title ?? "");
      setDescription(draft.data.description ?? "");
      setPriority(draft.data.priority ?? "");
      setSiteIds(draft.data.siteIds ?? []);
      setFormData(draft.data.formData ?? {});
      setShowDraftIndicator(true);
      setDraftLoaded(true);
    } else {
      setDraftLoaded(true);
    }
  }, [step, draftLoaded]);

  // Debounced auto-save function
  const debouncedSave = useCallback(
    debounce((stepNum: number) => {
      saveTicketDraft(stepNum, {
        categoryId,
        typeId,
        title,
        description,
        priority,
        siteIds,
        formData,
      });
    }, 500),
    [categoryId, typeId, title, description, priority, siteIds, formData]
  );

  // Auto-save draft when form data changes
  useEffect(() => {
    if (!draftLoaded) return;
    debouncedSave(step);
  }, [categoryId, typeId, title, description, priority, siteIds, formData, step, debouncedSave, draftLoaded]);

  // Clear draft when moving to next step or submitting
  const clearCurrentDraft = () => {
    clearTicketDraft(step);
    setShowDraftIndicator(false);
  };

  // Data queries
  const { data: categories = [] } = trpc.tickets.ticketCategories.getAll.useQuery();
  const { data: ticketTypes = [] } = trpc.tickets.ticketTypes.getAll.useQuery();
  const { data: queues = [] } = trpc.tickets.ticketQueues.getAll.useQuery();
  const { data: slaProfiles = [] } = trpc.tickets.slaPolicies.getAll.useQuery();
  const { data: sitesData } = trpc.sites.getAll.useQuery();
  const sites = Array.isArray(sitesData) ? sitesData : (sitesData as any)?.sites ?? [];

  const activeTypes = useMemo(
    () => ticketTypes.filter((tt: any) => tt.isActive !== false),
    [ticketTypes],
  );

  // Types are filtered by the chosen category (global "SLA demo" type always shows)
  const filteredTypes = useMemo(
    () =>
      categoryId
        ? activeTypes.filter(
            (tt: any) =>
              tt.categoryId === Number(categoryId) ||
              tt.name?.toLowerCase().includes("sla demo"),
          )
        : activeTypes,
    [activeTypes, categoryId],
  );

  const selectedType = ticketTypes.find((tt: any) => tt.id === Number(typeId));
  const selectedCategory = categories.find(
    (c: any) => c.id === Number(categoryId),
  );

  // Routing preview derived from the selected type
  const routing = useMemo(() => {
    if (!selectedType) return null;
    const queue = queues.find((q: any) => q.id === selectedType.queueId);
    const profile = queue
      ? slaProfiles.find((p: any) => p.id === queue.slaProfileId)
      : undefined;
    const p = priority;
    const responseHrs = profile ? (profile as any)[`${p}ResponseHrs`] : undefined;
    const resolutionHrs = profile
      ? (profile as any)[`${p}ResolutionHrs`]
      : selectedType.slaHours ?? undefined;
    const modeLabels: Record<string, string> = {
      round_robin: t("tickets.roundRobin", "Round robin"),
      load_balance: t("tickets.loadBalanced", "Load balanced"),
      skill_match: t("tickets.skillMatch", "Skill match"),
      manual: t("common.manual", "Manual"),
    };
    return {
      queueName: queue?.name ?? t("tickets.defaultQueue", "Default queue (auto-routed)"),
      response: fmtHours(responseHrs),
      resolution: fmtHours(resolutionHrs),
      mode: queue ? modeLabels[queue.assignmentMode] ?? t("common.auto", "Auto") : t("common.auto", "Auto"),
    };
  }, [selectedType, queues, slaProfiles, priority]);

  // Auto-set priority from queue default
  useEffect(() => {
    if (selectedType && priority === "") {
      const queue = queues.find((q: any) => q.id === selectedType.queueId);
      const defaultPriority = (queue as any)?.defaultPriority || "p3";
      setPriority(defaultPriority);
    }
  }, [selectedType, queues, priority]);

  // Check if dropdown should open above and calculate position
  useEffect(() => {
    if (!showSitesModal || !sitesButtonRef.current) return;

    const rect = sitesButtonRef.current.getBoundingClientRect();
    const dropdownHeight = 350;
    const spaceBelow = window.innerHeight - rect.bottom;

    setDropdownPos({
      top: rect.top,
      left: rect.left,
      width: rect.width,
    });

    if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
      setDropdownAbove(true);
    } else {
      setDropdownAbove(false);
    }
  }, [showSitesModal]);

  // Upload attachment mutation
  const uploadAttachmentMutation = trpc.tickets.uploadAttachment.useMutation();

  // Create mutation
  const createMutation = trpc.tickets.create.useMutation({
    onSuccess: async (data) => {
      // Upload attachments if any
      if (attachments.length > 0) {
        for (const file of attachments) {
          const reader = new FileReader();
          reader.onload = async (e) => {
            const base64 = (e.target?.result as string)?.split(',')[1] || '';
            try {
              await uploadAttachmentMutation.mutateAsync({
                ticketId: data.id,
                fileName: file.name,
                mimeType: file.type,
                base64Data: base64,
              });
            } catch (err) {
              console.error('Failed to upload attachment:', err);
            }
          };
          reader.readAsDataURL(file);
        }
      }
      setLocation(`/tickets/${data.id}`);
    },
  });

  const canProceed = () => {
    if (step === 0) return !!typeId;
    if (step === 1) return title.trim().length > 0;
    return true;
  };

  const handleSubmit = () => {
    const combined = { ...formData };
    const primarySiteId = siteIds.length > 0 ? Number(siteIds[0]) : undefined;
    clearCurrentDraft(); // Clear draft on successful submit
    createMutation.mutate({
      title,
      description: description || undefined,
      typeId: Number(typeId),
      priority: priority as any,
      siteId: primarySiteId,
      formData: Object.keys(combined).length > 0 ? combined : undefined,
    });
  };

  return (
    <div className="-mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100vh-4rem)] bg-white">
      <div className="px-6 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-1.5">
          <button
            onClick={() => setLocation("/tickets/all")}
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#6b6386] hover:bg-[#f5f3ff] hover:text-[#6d28d9] transition-colors"
            title={t("tickets.backToTickets", "Back to tickets")}
          >
            <ArrowRight className="h-4 w-4" />
          </button>
          <h1 className="text-2xl font-bold text-[#1a1530]">
            {t("tickets.createTicket")}
          </h1>
          {showDraftIndicator && draftLoaded && (
            <span className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#dbeafe] text-[#0369a1] text-[12px] font-semibold">
              <Clock className="h-3 w-3" />
              {t("tickets.draftAutoSaved")}
            </span>
          )}
        </div>
        <p className="text-[13.5px] text-[#6b6386] mb-6 ml-11">
          {t("tickets.fillInDetails")}
        </p>

        {/* Stepper — sliding segmented tabs + progress bar */}
        <div className="mb-8 isolate">
          <div className="relative flex rounded-xl p-[5px]" style={{ background: "#F1EFF7" }}>
            <div
              className="absolute top-[5px] bottom-[5px] rounded-[9px] bg-white"
              style={{
                width: `calc((100% - 10px) / ${STEPS.length})`,
                left: `calc(5px + (100% - 10px) / ${STEPS.length} * ${step})`,
                boxShadow: "0 2px 6px rgba(91,44,147,0.15)",
                transition: "left .35s cubic-bezier(.4,0,.2,1)",
              }}
            />
            {STEPS.map((s, i) => {
              const done = i < step;
              const active = i === step;
              return (
                <div
                  key={s.key}
                  className="relative z-10 flex flex-1 items-center justify-center gap-2 h-[38px] text-[13px] font-bold transition-colors"
                  style={{ color: active ? "#7C3AED" : "#515559" }}
                >
                  <span
                    className="flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-extrabold"
                    style={done || active ? { background: "#7C3AED", color: "#fff" } : { background: "#fff", color: "#8B84A3" }}
                  >
                    {done ? <Check className="h-3 w-3" /> : i + 1}
                  </span>
                  <span className="whitespace-nowrap">{t(`tickets.${s.label}`)}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-3 h-[5px] overflow-hidden rounded-full" style={{ background: "#F1EFF7" }}>
            <div
              className="h-full rounded-full"
              style={{ width: `${((step + 1) / STEPS.length) * 100}%`, background: "linear-gradient(90deg,#5B2C93,#7C3AED)", transition: "width .35s ease" }}
            />
          </div>
        </div>

        {/* Card */}
        <div className="bg-white border border-[#ece9f5] rounded-2xl p-8 md:p-10 shadow-[0_1px_2px_rgba(76,29,149,0.06)]">
          {/* ============ STEP 1 — CATEGORY & TYPE ============ */}
          {step === 0 && (
            <>
              <h2 className="text-lg font-bold text-[#1a1530] mb-1.5">{t("tickets.categoryAndType")}</h2>
              <p className="text-[13px] text-[#6b6386] mb-6">
                {t("tickets.pickCategoryThenType", "Pick a category, then the type that best matches your issue. SLA and routing depend on this.")}
              </p>

              {/* Category */}
              <label className="block text-[12.5px] font-semibold text-[#3d3554] mb-2">
                {t("tickets.category")}
              </label>
              <div className="flex flex-wrap gap-2 mb-6">
                {categories.length === 0 ? (
                  <p className="text-sm text-[#8b84a3]">{t("tickets.noCategoriesConfigured")}</p>
                ) : (
                  categories.map((c: any) => {
                    const active = Number(categoryId) === c.id;
                    return (
                      <button
                        key={c.id}
                        onClick={() => {
                          setCategoryId(String(c.id));
                          setTypeId("");
                          setFormData({});
                        }}
                        className={cn(
                          "px-3.5 py-2 rounded-lg border-2 text-[13px] font-semibold transition-all",
                          active
                            ? "border-[#7c3aed] bg-[#f5f3ff] text-[#6d28d9]"
                            : "border-[#ece9f5] text-[#3d3554] hover:border-[#c4b5fd] hover:bg-[#f5f3ff]",
                        )}
                      >
                        {c.name}
                      </button>
                    );
                  })
                )}
              </div>

              {/* Ticket Type */}
              <label className="block text-[12.5px] font-semibold text-[#3d3554] mb-2">
                {t("tickets.ticketType")} {!categoryId && <span className="text-[#8b84a3] font-normal">{t("tickets.selectCategoryFirst")}</span>}
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3.5">
                {filteredTypes.length === 0 ? (
                  <p className="col-span-full text-sm text-[#8b84a3] py-6 text-center">
                    {categoryId ? t("tickets.noTicketTypesInCategory") : t("tickets.noTicketTypesConfigured")}
                  </p>
                ) : (
                  filteredTypes.map((tt: any) => {
                    const v = typeVisual(tt.name);
                    const cat = categories.find((c: any) => c.id === tt.categoryId);
                    const Icon = v.icon;
                    const selected = Number(typeId) === tt.id;
                    return (
                      <button
                        key={tt.id}
                        onClick={() => {
                          setTypeId(String(tt.id));
                          setPriority(tt.defaultPriority ?? "p3");
                          setFormData({});
                        }}
                        className={cn(
                          "flex items-center gap-3 p-3.5 rounded-[10px] border-2 text-left transition-all",
                          selected
                            ? "border-[#7c3aed] bg-[#f5f3ff]"
                            : "border-[#ece9f5] hover:border-[#c4b5fd] hover:bg-[#f5f3ff]",
                        )}
                      >
                        <span
                          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: v.bg, color: v.fg }}
                        >
                          <Icon className="h-[18px] w-[18px]" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[13.5px] font-bold text-[#1a1530] truncate">
                            {tt.name}
                          </span>
                          <span className="block text-[11.5px] text-[#6b6386] truncate">
                            {cat?.name ?? `${t("common.default")} ${(tt.defaultPriority ?? "p3").toUpperCase()}`}
                            {tt.requiresApproval ? ` · ${t("tickets.requiresApproval", "requires approval")}` : ""}
                          </span>
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}

          {/* ============ STEP 2 — DETAILS ============ */}
          {step === 1 && (
            <>
              <h2 className="text-lg font-bold text-[#1a1530] mb-1.5">{t("tickets.details")}</h2>
              <p className="text-[13px] text-[#6b6386] mb-6">
                {t("tickets.provideClearSummary", "Provide a clear summary and description so the team can act quickly.")}
              </p>

              <div className="mb-[18px]">
                <label className="block text-[12.5px] font-semibold text-[#3d3554] mb-1.5">
                  {t("tickets.titleLabel")} <span className="text-[#ef4444]">*</span>
                </label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t("tickets.titlePlaceholder")}
                  className={inputCls}
                />
              </div>

              <div className="mb-[18px]">
                <label className="block text-[12.5px] font-semibold text-[#3d3554] mb-1.5">
                  {t("tickets.descriptionLabel")}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t("tickets.descriptionPlaceholder")}
                  className={cn(inputCls, "min-h-[140px] resize-y")}
                />
              </div>

              <div className="mb-[18px]">
                <label className="block text-[12.5px] font-semibold text-[#3d3554] mb-1.5">
                  {t("tickets.priorityLabel")} <span className="text-[#ef4444]">*</span>
                  {selectedType && <span className="text-[#6b6386] font-normal ml-1">({t("tickets.autoSelected")})</span>}
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {PRIORITIES.map((p) => {
                    const selected = priority === p.value;
                    const isLocked = !!selectedType;
                    return (
                      <button
                        key={p.value}
                        onClick={() => !isLocked && setPriority(p.value)}
                        disabled={isLocked}
                        className={cn(
                          "rounded-lg border-2 p-2.5 text-center font-bold text-[13px] transition-all",
                          isLocked
                            ? "cursor-not-allowed opacity-50"
                            : "",
                          selected
                            ? "border-[#7c3aed] bg-[#f5f3ff]"
                            : "border-[#ece9f5] hover:border-[#c4b5fd]",
                        )}
                        style={{ color: p.color }}
                        title={isLocked ? t("tickets.priorityAutoSelected") : undefined}
                      >
                        <span className="block text-base">{p.short}</span>
                        {t(`common.${p.label}`)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Dynamic fields from the selected ticket type */}
              {selectedType?.formFields && (selectedType.formFields as any[]).length > 0 && (
                <div className="border-t border-[#ece9f5] pt-4 mt-2 space-y-[18px]">
                  <p className="text-[11px] font-bold text-[#8b84a3] uppercase tracking-wider">
                    {t("tickets.additionalFields")}
                  </p>
                  {(selectedType.formFields as any[]).map((field: any) => (
                    <div key={field.name}>
                      <label className="block text-[12.5px] font-semibold text-[#3d3554] mb-1.5">
                        {field.label}
                        {field.required && <span className="text-[#ef4444]"> *</span>}
                      </label>
                      {field.type === "select" && field.options ? (
                        <select
                          value={String(formData[field.name] ?? "")}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, [field.name]: e.target.value }))
                          }
                          className={selectCls}
                          style={chevronBg}
                        >
                          <option value="">{`${t("common.select")} ${field.label}`}</option>
                          {field.options.map((opt: string) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      ) : field.type === "textarea" ? (
                        <textarea
                          value={String(formData[field.name] ?? "")}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, [field.name]: e.target.value }))
                          }
                          placeholder={field.label}
                          className={cn(inputCls, "min-h-[70px] resize-y")}
                        />
                      ) : (
                        <input
                          type={field.type === "number" ? "number" : "text"}
                          value={String(formData[field.name] ?? "")}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, [field.name]: e.target.value }))
                          }
                          placeholder={field.label}
                          className={inputCls}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Sites Selection - Two-Pane Modal */}
              <div className="mt-[18px]">
                <label className="block text-[12.5px] font-semibold text-[#3d3554] mb-1.5">
                  {t("tickets.sitesLabel")}
                </label>
                <button
                  ref={sitesButtonRef}
                  type="button"
                  onClick={() => setShowSitesModal(true)}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#ece9f5] bg-white text-[13.5px] text-[#1a1530] outline-none focus:border-[#8b5cf6] focus:shadow-[0_0_0_3px_#ede9fe] transition text-left flex items-center justify-between hover:bg-[#fafaff]"
                >
                  <span>
                    {siteIds.length === 0 ? (
                      <span className="text-[#b8b2c9]">{t("tickets.selectSitesPlaceholder")}</span>
                    ) : (
                      <span className="text-[13px]">
                        {t("tickets.sitesSelected", { count: siteIds.length })}
                      </span>
                    )}
                  </span>
                  <span className="text-[#6b6386]">▼</span>
                </button>

                {/* Two-Pane Modal */}
                {showSitesModal && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 bg-black/50 z-40"
                      onClick={() => setShowSitesModal(false)}
                    />

                    {/* Modal */}
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                      <div className="bg-white rounded-lg w-[420px] h-[420px] flex flex-col overflow-hidden shadow-2xl">
                        {/* Modal Header */}
                        <div className="px-5 py-3.5 border-b border-[#ece9f5] flex items-center justify-between">
                          <h2 className="text-[14px] font-semibold text-[#1a1530]">{t("tickets.selectSitesPlaceholder")}</h2>
                          <button
                            onClick={() => setShowSitesModal(false)}
                            className="text-[#999] hover:text-[#1a1530] text-xl leading-none w-6 h-6 flex items-center justify-center"
                          >
                            ✕
                          </button>
                        </div>

                        {/* Search Bar */}
                        <div className="px-4 py-3 border-b border-[#ece9f5] bg-white">
                          <input
                            type="text"
                            placeholder={t("tickets.searchSitesPlaceholder")}
                            value={sitesSearchQuery}
                            onChange={(e) => setSitesSearchQuery(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-[#ece9f5] bg-white text-[12px] text-[#1a1530] placeholder:text-[#b8b2c9] outline-none focus:border-[#8b5cf6] focus:shadow-[0_0_0_3px_#ede9fe]"
                            autoFocus
                          />
                        </div>

                        {/* Selected Sites Section */}
                        {siteIds.length > 0 && (
                          <div className="px-3 py-1.5 border-b border-[#ece9f5] bg-[#f0ecf8]">
                            <h3 className="text-[9px] font-bold text-[#5b2c93] uppercase tracking-wider mb-1">
                              {t("tickets.selected", { count: siteIds.length })}
                            </h3>
                            <div className="flex flex-wrap gap-1">
                              {sites
                                .filter((s: any) => siteIds.includes(String(s.id)))
                                .map((s: any) => (
                                  <div
                                    key={s.id}
                                    className="flex items-center gap-1 px-1.5 py-0.5 bg-[#ede9fe] rounded text-[9px] font-medium text-[#5b2c93]"
                                  >
                                    <span className="truncate max-w-[100px]">{s.name}</span>
                                    <button
                                      onClick={() => setSiteIds(siteIds.filter(id => id !== String(s.id)))}
                                      className="hover:text-[#4a1f75] flex-shrink-0 text-[8px] leading-none"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}

                        {/* Available Sites List - Full Width */}
                        <div className="flex flex-col flex-1 overflow-hidden">
                          <div className="px-4 py-2.5 border-b border-[#ece9f5] bg-[#f0ecf8]">
                            <h3 className="text-[10px] font-bold text-[#5b2c93] uppercase tracking-wider">
                              {t("tickets.availableSites")}
                            </h3>
                          </div>
                          <div className="flex-1 overflow-y-auto">
                            {sites.filter((s: any) => s.name.toLowerCase().includes(sitesSearchQuery.toLowerCase()) && !siteIds.includes(String(s.id))).length === 0 ? (
                              <div className="text-center text-[#bbb] text-[11px] py-8">
                                {sitesSearchQuery ? t("tickets.noSitesFound") : siteIds.length > 0 ? t("tickets.allSitesSelected") : t("tickets.noSitesAvailable")}
                              </div>
                            ) : (
                              <div className="divide-y divide-[#ece9f5]">
                                {sites
                                  .filter((s: any) => s.name.toLowerCase().includes(sitesSearchQuery.toLowerCase()) && !siteIds.includes(String(s.id)))
                                  .map((s: any) => (
                                    <label
                                      key={s.id}
                                      className="flex items-center gap-2.5 px-4 py-2.5 cursor-pointer hover:bg-[#fafaff] transition-colors text-[11px]"
                                    >
                                      <input
                                        type="checkbox"
                                        onChange={() => setSiteIds([...siteIds, String(s.id)])}
                                        className="w-3.5 h-3.5 rounded border-[#ece9f5] cursor-pointer accent-[#5b2c93] flex-shrink-0"
                                      />
                                      <span className="text-[#3d3554] flex-1 truncate">
                                        {s.name}
                                      </span>
                                    </label>
                                  ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-4 py-3 border-t border-[#ece9f5] flex gap-2.5 justify-end bg-[#fafaff]">
                          <button
                            onClick={() => setShowSitesModal(false)}
                            className="px-3.5 py-1.5 text-[11px] font-semibold text-[#3d3554] bg-white border border-[#ece9f5] rounded hover:bg-[#f5f3ff] transition"
                          >
                            {t("common.cancel")}
                          </button>
                          <button
                            onClick={() => setShowSitesModal(false)}
                            className="px-3.5 py-1.5 text-[11px] font-semibold text-white bg-[#5b2c93] rounded hover:bg-[#4a1f75] transition"
                          >
                            {t("common.done")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Attachments */}
              <div className="mt-[18px]">
                <label className="block text-[12.5px] font-semibold text-[#3d3554] mb-1.5">
                  {t("tickets.attachmentsLabel")}
                </label>
                <div className="border-2 border-dashed border-[#ddd6fe] rounded-[10px] py-6 px-4 text-center text-[#8b84a3] cursor-pointer hover:border-[#c4b5fd] hover:bg-[#f9f8ff] transition">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      const validFiles = files.filter((f) => f.size <= 25 * 1024 * 1024);
                      if (validFiles.length < files.length) {
                        toast.error(t("tickets.filesExceedLimit", "Some files exceed 25MB limit"));
                      }
                      setAttachments((prev) => [...prev, ...validFiles]);
                    }}
                    className="hidden"
                    id="attachment-input"
                  />
                  <label htmlFor="attachment-input" className="cursor-pointer block">
                    <Upload className="h-7 w-7 mx-auto mb-1.5" />
                    <div className="text-[13px] font-semibold text-[#3d3554]">
                      {t("tickets.clickToUploadOrDrag")}
                    </div>
                    <div className="text-[11.5px] mt-0.5">
                      {t("tickets.screenshotsLogsPdfs")}
                    </div>
                  </label>
                </div>
                {attachments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {attachments.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2.5 bg-[#f9f8ff] rounded-lg border border-[#ece9f5]">
                        <span className="text-[12.5px] text-[#3d3554] font-medium truncate">
                          {file.name}
                        </span>
                        <button
                          onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== idx))}
                          className="text-[#ef4444] text-[12px] font-semibold hover:underline"
                        >
                          {t("tickets.remove")}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ============ STEP 3 — REVIEW & SUBMIT ============ */}
          {step === 2 && (
            <>
              <h2 className="text-lg font-bold text-[#1a1530] mb-1.5">{t("tickets.reviewAndSubmit")}</h2>
              <p className="text-[13px] text-[#6b6386] mb-6">
                {t("tickets.confirmDetails")}
              </p>

              {/* Summary grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 rounded-[10px] border border-[#ece9f5] bg-[#fafaff] px-5 py-4 mb-5">
                {[
                  { l: t("tickets.category"), v: selectedCategory?.name ?? "—" },
                  { l: t("tickets.type"), v: selectedType?.name ?? "—" },
                  { l: t("tickets.priority"), v: PRIORITIES.find((p) => p.value === priority) ? t(`common.${PRIORITIES.find((p) => p.value === priority)?.label}`) : priority.toUpperCase() },
                  { l: t("tickets.title"), v: title || "—" },
                  { l: t("tickets.sites"), v: siteIds.length > 0 ? sites.filter((s: any) => siteIds.includes(String(s.id))).map((s: any) => s.name).join(", ") : t("tickets.noSitesSelected") },
                ].map((row) => (
                  <div key={row.l}>
                    <div className="text-[11px] font-bold text-[#8b84a3] uppercase tracking-wide">{row.l}</div>
                    <div className="text-[13.5px] font-semibold text-[#1a1530] mt-0.5 break-words">{row.v}</div>
                  </div>
                ))}
              </div>

              {/* Description preview */}
              {description && (
                <div className="bg-white border border-[#ece9f5] rounded-[10px] px-5 py-4 mb-5">
                  <div className="text-[11px] font-bold text-[#8b84a3] uppercase tracking-wide mb-2.5">{t("tickets.description")}</div>
                  <div className="text-[13.5px] text-[#3d3554] leading-relaxed whitespace-pre-wrap line-clamp-3">
                    {description}
                  </div>
                </div>
              )}

              {/* Routing preview */}
              <div className="bg-[#f5f3ff] border border-[#ddd6fe] rounded-[10px] px-4 py-3.5">
                <div className="text-[11px] font-bold text-[#6d28d9] uppercase tracking-wider mb-2.5">
                  {t("tickets.routingPreview")}
                </div>
                <div className="flex flex-col gap-2 text-[13px] text-[#3d3554]">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-[#6d28d9] flex-shrink-0" />
                    <span>
                      <b>{t("tickets.queue")}:</b> {routing?.queueName ?? "—"}
                    </span>
                  </div>
                  {routing?.response && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[#6d28d9] flex-shrink-0" />
                      <span>
                        <b>{t("tickets.responseSla")}:</b> {routing.response}
                      </span>
                    </div>
                  )}
                  {routing?.resolution && (
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-[#6d28d9] flex-shrink-0" />
                      <span>
                        <b>{t("tickets.resolutionSla")}:</b> {routing.resolution} ({priority.toUpperCase()})
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <UserCheck className="h-4 w-4 text-[#6d28d9] flex-shrink-0" />
                    <span>
                      <b>{t("tickets.assignment")}:</b> {routing?.mode ?? t("common.auto")}
                    </span>
                  </div>
                </div>
              </div>

              {createMutation.isError && (
                <p className="mt-3 text-[13px] text-[#dc2626]">
                  {(createMutation.error as any)?.message ?? t("tickets.failedToCreate", "Failed to create ticket")}
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer nav */}
        <div className="flex items-center justify-end gap-3 mt-6">
          <button
            onClick={() =>
              step === 0 ? setLocation("/tickets/all") : setStep((s) => Math.max(0, s - 1))
            }
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#ece9f5] bg-white text-[13px] font-semibold text-[#3d3554] hover:bg-[#f5f3ff] hover:border-[#ddd6fe] hover:text-[#6d28d9] transition-colors"
          >
            {step === 0 ? (
              t("common.cancel")
            ) : (
              <>
                <ArrowRight className="h-4 w-4" />
                {t("common.back")}
              </>
            )}
          </button>

          {step < STEPS.length - 1 ? (
            <button
              onClick={() => setStep((s) => s + 1)}
              disabled={!canProceed()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#7c3aed] hover:bg-[#6d28d9] text-white text-[13px] font-semibold shadow-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {step === 0 ? t("tickets.nextToDetails") : t("tickets.nextToReview")}
              <ArrowLeft className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={createMutation.isPending}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-[#7c3aed] hover:bg-[#6d28d9] text-white text-[14px] font-semibold shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("tickets.creating")}
                </>
              ) : (
                t("tickets.createTicket")
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
